import React, { useState } from 'react';
import cn from 'classnames';

import { SpotlightsActionMenu } from './SpotlightsMenus';
import { getSmartImage } from '../../helpers/helpers';
import { fmtMoney, fmtRoas, STATUS_LABELS, toDisplayStatus } from '../../helpers/spotlights_dashboard_helpers';
import './SpotlightsSubTable.scss';

// A clearly-nested "subtable" of spotlights shown inside an expanded brand/creator row.
// Styled after the user-facing SpotlightTableRow: the associated mention's content thumbnail,
// identity + a colored status display, and AN ROAS rendered as a green/red metric pill.
// Collapses to the first few rows with View more / View all / View less controls.

const COLLAPSED_COUNT = 5;
const STEP = 5;

// Status → accent color (matches SpotlightTableRow / SpotlightStyles status hues).
const STATUS_COLOR = {
  uploading: '#767676',
  readyToLaunch: '#b5740a',
  launching: '#1a73e8',
  live: '#11835a',
  calibrating: '#8a6d1f',
  paused: '#767676',
  blocked: '#da0e3f',
  complete: '#767676'
};

const roasPillClass = (roas, spend) => {
  const r = Number(roas) || 0;
  const s = Number(spend) || 0;
  if (s > 0 && r >= 2) return 'green';
  if (s > 0 && r < 1) return 'red';
  return '';
};

// columns: [{ key, label, num?, className?, render(s) }]. The first column is the identity
// column (thumbnail + name + status display) and gets extra width.
const SpotlightsSubTable = ({ title = 'Spotlights', spotlights = [], identityLabel, getIdentity, extraColumns = [], getActions }) => {
  const [limit, setLimit] = useState(COLLAPSED_COUNT);
  const total = spotlights.length;
  const visible = spotlights.slice(0, limit);

  const columns = [
    { key: 'identity', label: identityLabel, className: 'identity' },
    { key: 'budget', label: 'Daily budget', num: true, render: s => (s.dailyBudget ? fmtMoney(s.dailyBudget) : '—') },
    { key: 'spend', label: 'Spend', num: true, render: s => fmtMoney(s.spend) },
    {
      key: 'roas',
      label: 'AN ROAS',
      num: true,
      render: s => <span className={cn('sl-pill', roasPillClass(s.roasAdNetwork, s.spend))}>{fmtRoas(s.roasAdNetwork)}</span>
    },
    ...extraColumns,
    ...(getActions ? [{ key: 'actions', label: '', className: 'actions', render: s => <SpotlightsActionMenu options={getActions(s)} /> }] : [])
  ];

  // One shared grid template for the header row and every data row → columns always align.
  const gridTemplateColumns = columns
    .map(c => (c.key === 'identity' ? 'minmax(0, 2.4fr)' : c.key === 'actions' ? '40px' : 'minmax(0, 1fr)'))
    .join(' ');

  return (
    <div className='sl-subtable'>
      <div className='sl-subtable-head'>
        <span className='sl-subtable-title'>
          {title} <span className='sl-subtable-count'>{total}</span>
        </span>
      </div>

      <div className='sl-subtable-cols' style={{ gridTemplateColumns }}>
        {columns.map(c => (
          <div key={c.key} className={cn('sl-subcol-head', c.className, { num: c.num })}>
            {c.label}
          </div>
        ))}
      </div>

      <div className='sl-subtable-body'>
        {!total && <div className='sl-empty'>No spotlights.</div>}
        {visible.map(s => (
          <div className='sl-subrow' key={s.id} style={{ gridTemplateColumns }}>
            {columns.map(c => {
              if (c.key === 'identity') {
                const displayStatus = toDisplayStatus(s.status);
                const statusColor = STATUS_COLOR[displayStatus] || '#767676';
                return (
                  <div key={c.key} className='sl-subcol identity'>
                    <div className='sl-subrow-identity'>
                      <div className='sl-subrow-thumb'>{s.thumbnail && <img className={cn({ contain: s.thumbnailIsLogo })} src={getSmartImage(s.thumbnail)} alt='' />}</div>
                      <div className='sl-subrow-idtext'>
                        <div className='sl-subrow-name'>{getIdentity(s)}</div>
                        <div className='sl-subrow-status' style={{ color: statusColor }}>
                          <span className='dot' style={{ background: statusColor }} />
                          {STATUS_LABELS[displayStatus] || s.status}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <div key={c.key} className={cn('sl-subcol', { num: c.num })}>
                  {c.render(s)}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {total > COLLAPSED_COUNT && (
        <div className='sl-subtable-more'>
          {limit < total && (
            <button className='sl-more-btn' onClick={() => setLimit(l => Math.min(l + STEP, total))}>
              View More
            </button>
          )}
          {limit < total && (
            <button className='sl-more-btn' onClick={() => setLimit(total)}>
              View All ({total})
            </button>
          )}
          {limit >= total && (
            <button className='sl-more-btn' onClick={() => setLimit(COLLAPSED_COUNT)}>
              View Less
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SpotlightsSubTable;
